// Data check: every included table's rows against its dictionary and the design's keys, all
// tables at once (GET /api/projects/{id}/dbdesign/data-check; backend unio/validation/ through
// services/data_check.py). Errors block Build — the server refuses a build with the same
// sentences — warnings and notes do not. Run on demand, because the server reads every file.
//
// The list can be narrowed to a study and a table; each row links to the dataset's Columns
// tab, where its author fixes the dictionary, and the report downloads as XLSX (the table's own
// rows with the bad cells highlighted) for fixing the file itself, then uploading it with Replace.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api";
import { ErrorNote, Loading } from "../../components/Feedback";
import { Badge, Btn } from "../../components/Shared";

const cell = { padding: "6px 10px", borderTop: "1px solid #f3f4f6", verticalAlign: "top", fontSize: 13 };
const head = { padding: "8px 10px", textAlign: "left", fontSize: 12, color: "#6b7280", textTransform: "uppercase",
               letterSpacing: "0.05em", whiteSpace: "nowrap" };
const label = { fontSize: 12, fontWeight: 600, color: "#374151", letterSpacing: "0.05em", textTransform: "uppercase" };
const select = { background: "#fff", border: "1px solid #d1d5db", borderRadius: 8, padding: "8px 10px", fontSize: 14,
                 fontFamily: "inherit", minWidth: 180 };
const SEVERITY = { error: "red", warning: "amber", info: "gray" };
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

export default function DataCheckPanel({ projectId, slug }) {
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [study, setStudy] = useState("");
  const [table, setTable] = useState("");
  const [downloadError, setDownloadError] = useState(null);
  const base = `/projects/${projectId}/dbdesign`;

  const run = async () => {
    setBusy(true);
    setError(null);
    try { setReport(await api.get(`${base}/data-check`)); } catch (e) { setError(e); } finally { setBusy(false); }
  };
  const download = name => {
    setDownloadError(null);
    const q = name ? `?table=${encodeURIComponent(name)}` : "";
    api.download(`${base}/data-check.xlsx${q}`, `${slug || "project"}-data-check${name ? `-${name}` : ""}.xlsx`)
      .catch(setDownloadError);
  };

  const tables = useMemo(() => Object.fromEntries((report?.tables || []).map(t => [t.name, t])), [report]);
  // The study filter: the study key (`study`) is the value, its name the label.
  const studies = useMemo(() => {
    const names = new Map();
    (report?.tables || []).forEach(t => { if (t.study) names.set(t.study, t.study_name || t.study); });
    return [...names].map(([value, name]) => ({ value, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [report]);
  const studyName = studies.find(s => s.value === study)?.name;
  const tableNames = (report?.tables || []).filter(t => !study || t.study === study).map(t => t.name);
  const shown = (report?.issues || []).filter(i => (!study || tables[i.table]?.study === study)
                                                && (!table || i.table === table));
  const counts = report?.counts || { error: 0, warning: 0, info: 0 };

  return (
    <div>
      <p style={{ margin: "0 0 12px", fontSize: 13, color: "#374151", maxWidth: 820 }}>
        Checks every row of every included table against its column dictionary and the design's keys:
        wrong types, empty or repeated keys, values another study's table does not have. Errors block
        Build; warnings and notes do not.
      </p>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        <Btn variant={report ? "secondary" : "primary"} onClick={run} disabled={busy}>
          {busy ? "Checking the data…" : report ? "Check again" : "Check the data"}
        </Btn>
        {report && (
          <Btn variant="ghost" onClick={() => download(null)}>Download the report (.xlsx)</Btn>
        )}
        {report && table && (
          <Btn variant="ghost" onClick={() => download(table)}>Download {table} (.xlsx)</Btn>
        )}
      </div>
      {busy && <Loading label="Reading every table…" />}
      <ErrorNote error={error} />
      <ErrorNote error={downloadError} />
      {report && (
        <>
          <div role="status" aria-live="polite" style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
            {counts.error + counts.warning + counts.info === 0
              ? <Badge color="green">No problems found in {plural(report.tables.length, "table")}</Badge>
              : <>
                  <Badge color={counts.error ? "red" : "green"}>{plural(counts.error, "error")}</Badge>
                  <Badge color="amber">{plural(counts.warning, "warning")}</Badge>
                  {counts.info > 0 && <Badge color="gray">{plural(counts.info, "note")}</Badge>}
                </>}
          </div>
          {report.problems.length > 0 && (
            <ul aria-label="What blocks Build" style={{ margin: "0 0 14px", paddingLeft: 18, fontSize: 13, color: "#991b1b" }}>
              {report.problems.map(p => <li key={p}>{p}</li>)}
            </ul>
          )}
          {report.issues.length > 0 && (
            <>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 12 }}>
                <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <span style={label}>Study</span>
                  <select style={select} value={study} onChange={e => { setStudy(e.target.value); setTable(""); }}>
                    <option value="">All studies</option>
                    {studies.map(s => <option key={s.value} value={s.value}>{s.name}</option>)}
                  </select>
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <span style={label}>Table</span>
                  <select style={select} value={table} onChange={e => setTable(e.target.value)}>
                    <option value="">All tables</option>
                    {tableNames.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
              </div>
              <div data-table-scroll style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 12, background: "#fff" }}>
                <table style={{ borderCollapse: "collapse", width: "100%" }}>
                  <caption style={{ position: "absolute", left: -9999 }}>
                    Data check findings{study ? ` of ${studyName}` : ""}{table ? ` in ${table}` : ""}: {shown.length} shown
                  </caption>
                  <thead><tr>
                    <th scope="col" style={head}>Severity</th><th scope="col" style={head}>Table</th>
                    <th scope="col" style={head}>Row</th><th scope="col" style={head}>Column</th>
                    <th scope="col" style={head}>Value</th><th scope="col" style={head}>Problem</th>
                  </tr></thead>
                  <tbody>
                    {shown.map((i, n) => {
                      const t = tables[i.table];
                      return (
                        <tr key={n}>
                          <td style={cell}><Badge color={SEVERITY[i.severity] || "gray"}>{i.severity}</Badge></td>
                          <td style={cell}>
                            {t && t.dataset_id
                              ? <Link to={`/datasets/${t.dataset_id}/columns`} title={`Columns of ${t.dataset_name}`}>{i.table}</Link>
                              : i.table}
                            {t?.study && <div style={{ fontSize: 12, color: "#6b7280" }}>{t.study_name || t.study}</div>}
                          </td>
                          <td style={cell}>{i.row ?? "—"}</td>
                          <td style={cell}>{i.column ?? "—"}</td>
                          <td style={{ ...cell, fontFamily: "'JetBrains Mono', monospace", whiteSpace: "pre-wrap" }}>{i.value ?? ""}</td>
                          <td style={cell}>
                            {i.message}
                            <div style={{ fontSize: 12, color: "#6b7280" }}>{report.rules?.[i.rule]?.title || i.rule}</div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {report.truncated && (
                <p style={{ fontSize: 12, color: "#6b7280" }}>
                  At most {report.cap_per_rule} findings of one kind per table are listed; the .xlsx report marks all of them.
                </p>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
